import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import ImageUnit from "../../../../shared/units/ImageUnit";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import SanitizeDialogFields from "../../field/document/SanitizeDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	SanitizeAnnotations,
	SanitizeAttachments,
	SanitizeForms,
	SanitizeLayers,
	SanitizeMetadata,
	SanitizeOutlines,
	SanitizePages,
	SanitizeTags,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxRedactRedact
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {
	SanitizeActions
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources/operation/SanitizeActions";

interface SanitizeDialogStatus extends PortalDialogState<ToolboxRedactRedact> {
	pages: number;
	currentPage: string;
}

export class SanitizeDialogDefinition extends PortalDialog<ToolboxRedactRedact,
	PortalDialogProperties<ToolboxRedactRedact>, SanitizeDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<SanitizeDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.sanitize, Functions.getSanitizeDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SANITIZE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxRedactRedact | undefined = this.state.parameter?.data;
		let errors: Map<SanitizeDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.ANNOTATIONS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-annotations"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-annotations"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.annotations?.enabled,
											 SanitizeAnnotations.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.ANNOTATIONS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-annotations">
								<Translate {...this.props} data="sanitize.settings.annotations.label"
										   namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.annotations.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.ATTACHMENTS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-attachments"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-attachments"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.attachments?.enabled,
											 SanitizeAttachments.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.ATTACHMENTS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-attachments">
								<Translate {...this.props} data="sanitize.settings.attachments.label"
										   namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.attachments.info"
									  options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.FORMS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-forms"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-forms"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.forms?.enabled, SanitizeForms.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.FORMS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-forms">
								<Translate {...this.props} data="sanitize.settings.forms.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.forms.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.LAYERS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-layers"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-layers"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.layers?.enabled, SanitizeLayers.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.LAYERS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-layers">
								<Translate {...this.props} data="sanitize.settings.layers.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.layers.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.METADATA)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-metadata"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-metadata"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.metadata?.enabled,
											 SanitizeMetadata.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.METADATA, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-metadata">
								<Translate {...this.props} data="sanitize.settings.metadata.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.metadata.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.OUTLINES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-outlines"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-outlines"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.outlines?.enabled,
											 SanitizeOutlines.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.OUTLINES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-outlines">
								<Translate {...this.props} data="sanitize.settings.outlines.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.outlines.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.TAGS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-tags"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-tags"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.tags?.enabled, SanitizeTags.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.TAGS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-tags">
								<Translate {...this.props} data="sanitize.settings.tags.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.tags.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.PAGES)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pages"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-pages"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.pages?.enabled, SanitizePages.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.PAGES, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-pages">
								<Translate {...this.props} data="sanitize.settings.pages.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.pages.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.ACTIONS)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-actions"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-actions"
										 checked={FunctionHelper.applyValue(
											 parameter?.sanitize?.actions?.enabled, SanitizeActions.getEnabledDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.ACTIONS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-actions">
								<Translate {...this.props} data="sanitize.settings.actions.label" namespace="dialog"/>
								<Info {...this.props} data="sanitize.settings.actions.info" options={{ns: "dialog"}}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!FunctionHelper.applyValue(
							  parameter?.sanitize?.pages?.enabled, SanitizePages.getEnabledDefault()
						  )}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SanitizeDialogFields.PAGERANGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
							<Translate {...this.props} data="sanitize.pages.selection.label" namespace="dialog"/>
							<Info {...this.props} data="sanitize.pages.selection.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<PageRange inputId="control-pagerange"
									   className={errors.has(SanitizeDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.sanitize?.pages?.pages, SanitizePages.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   SanitizeDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(SanitizeDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(SanitizeDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(SanitizeDialogFields.PAGERANGE) ? Translate.value(
								errors.get(SanitizeDialogFields.PAGERANGE)!, {ns: "message"}
							) : "")}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SanitizeDialogFields.DPI)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-dpi">
							<Translate {...this.props} data="sanitize.pages.dpi.label" namespace="dialog"/>
							<Info {...this.props} data="sanitize.pages.dpi.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-dpi" step={1} suffix={" " + ImageUnit.DPI} showButtons={true}
										 useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.sanitize?.pages?.dpi, SanitizePages.getDpiDefault()
										 )}
										 min={SanitizePages.getDpiMin()} max={SanitizePages.getDpiMax()}
										 onChange={(e) => this.onChange(
											 SanitizeDialogFields.DPI, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["sanitize-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.sanitize.general"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: SanitizeDialogFields, value: any): void {
		let parameter: ToolboxRedactRedact = this.state.parameter!.data;
		let errors: Map<SanitizeDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case SanitizeDialogFields.ANNOTATIONS:
				parameter.sanitize!.annotations!.enabled = value;
				break;
			case SanitizeDialogFields.ATTACHMENTS:
				parameter.sanitize!.attachments!.enabled = value;
				break;
			case SanitizeDialogFields.FORMS:
				parameter.sanitize!.forms!.enabled = value;
				break;
			case SanitizeDialogFields.LAYERS:
				parameter.sanitize!.layers!.enabled = value;
				break;
			case SanitizeDialogFields.METADATA:
				parameter.sanitize!.metadata!.enabled = value;
				break;
			case SanitizeDialogFields.OUTLINES:
				parameter.sanitize!.outlines!.enabled = value;
				break;
			case SanitizeDialogFields.TAGS:
				parameter.sanitize!.tags!.enabled = value;
				break;
			case SanitizeDialogFields.PAGES:
				if (!value) {
					errors.delete(SanitizeDialogFields.PAGERANGE);
				} else {
					this.onChange(SanitizeDialogFields.PAGERANGE, parameter.sanitize!.pages!.pages);
				}

				parameter.sanitize!.pages!.enabled = value;
				break;
			case SanitizeDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.sanitize!.pages!.pages = value;
				break;
			case SanitizeDialogFields.DPI:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SanitizePages.getDpiDefault();
				}

				parameter.sanitize!.pages!.dpi = value;
				break;
			case SanitizeDialogFields.ACTIONS:
				parameter.sanitize!.actions!.enabled = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-redact-sanitize small"}/>
					<Translate {...this.props} data="sanitize.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxRedactRedact | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.sanitize!.pages!.pages = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			redact: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.redact.sanitize", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SANITIZE;
	}

	protected getDefaultParameter(): Parameter<ToolboxRedactRedact> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const SanitizeDialog = React.memo(SanitizeDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxRedactRedact>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxRedactRedact>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SanitizeDialog;